import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import FaqAccordion from '@/components/feature/FaqAccordion';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import {
  aboutTestimonials,
  aboutTimeline,
  contentPrinciples,
  discussionRules,
} from '@/mocks/community';
import { faqItems } from '@/mocks/gossip';

const anchorNav = [
  { href: '#position', label: '网站定位', icon: 'ri-compass-3-line' },
  { href: '#timeline', label: '发展理念', icon: 'ri-road-map-line' },
  { href: '#principles', label: '内容原则', icon: 'ri-book-2-line' },
  { href: '#voices', label: '读者口碑', icon: 'ri-chat-quote-line' },
  { href: '#faq', label: '常见问题', icon: 'ri-question-line' },
  { href: '#contact', label: '联系我们', icon: 'ri-mail-line' },
];

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们',
    description: '介绍爆料吃瓜的网站定位、发展理念、内容原则与联系方式。',
    inLanguage: 'zh-CN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '爆料吃瓜',
    email: 'contact@baoliaochigua.com',
    description: '以网友讨论为基础的娱乐热点整理站点，内容不构成结论性评价。',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: '/about' },
    ],
  },
];

export default function About() {
  useDocumentMeta({
    title: '关于我们 - 网站定位、内容原则与联系方式 | 爆料吃瓜',
    description:
      '了解爆料吃瓜的网站定位、发展理念与内容原则：我们整理公开线索与网友投稿，标注来源与证据完整度，不做结论性评价，并长期接受内容更正与隐私相关请求。',
    keywords: '关于爆料吃瓜,内容原则,网站定位,爆料吃瓜联系方式,内容更正',
    path: '/about',
    schema,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: '关于我们' }]} />
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-[42px]">
              关于我们
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              爆料吃瓜是一个以网友讨论为基础的娱乐热点整理站点。我们相信，大多数时候大家需要的不是一个「最终结论」，
              而是一条足够清楚的时间线，以及一个能让人把不同看法说出来的地方。
            </p>

            <nav aria-label="页面锚点导航" className="mt-7 flex flex-wrap gap-2">
              {anchorNav.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className={item.icon} aria-hidden="true" />
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        </section>

        <section id="position" className="mx-auto w-full scroll-mt-24 px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.1fr_1fr] lg:gap-12">
            <Reveal>
              <SectionHeading
                eyebrow="Position"
                title="一个把「不确定」写出来的站点"
                desc="我们不假装知道所有事情。材料不足时，会直接告诉你材料不足。"
              />
              <div className="mt-6 flex flex-col gap-4 text-sm leading-relaxed text-foreground-700">
                <p>
                  很多同类内容的问题不在于信息少，而在于它们把推测写成了事实。一句话结论看起来痛快，但读者失去了自己判断的机会。
                  所以我们选择把过程摊开：哪一条来自视频、哪一条只是截图、哪一条完全没法验证。
                </p>
                <p>
                  这让阅读成本变高了一点，也让内容没那么「爽」。但我们认为，愿意多花两分钟看懂时间线的人，才是真正想搞清楚事情的人。
                </p>
              </div>

              <dl className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3">
                {[
                  { label: '仍在跟踪', value: '48 条' },
                  { label: '累计观点', value: '6.8 万' },
                  { label: '社群成员', value: '3.2 万' },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                    <dt className="text-[11px] text-foreground-500">{item.label}</dt>
                    <dd className="mt-1 font-heading text-lg font-black text-foreground-950">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>

            <Reveal delay={70}>
              <div className="h-[280px] w-full overflow-hidden rounded-lg md:h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Cozy%20archive%20desk%20scene%20with%20open%20notebook%2C%20scattered%20printed%20pages%20and%20a%20small%20desk%20lamp%2C%20warm%20cream%20and%20terracotta%20color%20palette%20with%20soft%20teal%20accents%2C%20natural%20window%20light%20and%20gentle%20shadows%2C%20editorial%20still%20life%20photography%2C%20calm%20orderly%20mood%2C%20no%20readable%20text%2C%20no%20people&width=1100&height=1200&seq=about-archive-desk-01&orientation=portrait"
                  alt="暖色书桌上摊开的笔记本与打印资料，象征线索整理与核对"
                  title="爆料吃瓜 · 关于我们"
                  className="h-full w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </section>

        <section id="timeline" className="w-full scroll-mt-24 bg-secondary-950 py-12 md:py-16">
          <div className="mx-auto w-full px-4 md:px-6">
            <Reveal>
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.28em] text-accent-400">
                  <span className="h-px w-6 bg-accent-500" />
                  Our Story
                </span>
                <h2 className="mt-3 font-heading text-2xl font-black leading-tight text-background-50 md:text-[32px]">
                  一路走来，只把一件事做得更细一点
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-background-200/80">
                  从几十人的小群，到现在几万人的讨论区，变的只是人数，没有变的是那套整理方法。
                </p>
              </div>
            </Reveal>

            <ol className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              {aboutTimeline.map((item, index) => (
                <Reveal key={item.time} delay={index * 70}>
                  <li className="flex h-full flex-col rounded-lg border border-secondary-800 bg-secondary-900/60 p-5">
                    <span className="font-heading text-sm font-black text-accent-400">{item.time}</span>
                    <span className="mt-3 h-px w-full bg-secondary-800" />
                    <p className="mt-3 text-[13px] leading-relaxed text-background-300/85">{item.text}</p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </section>

        <section id="principles" className="mx-auto w-full scroll-mt-24 px-4 py-12 md:px-6 md:py-16">
          <Reveal>
            <SectionHeading
              eyebrow="Principles"
              title="六条内容原则"
              desc="它们不是口号，而是我们每次发一条内容前会对照检查的清单。"
            />
          </Reveal>
          <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {contentPrinciples.map((item, index) => (
              <Reveal key={item.id} delay={index * 55}>
                <div className="flex h-full gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className={`${item.icon} text-lg`} aria-hidden="true" />
                  </span>
                  <span>
                    <h3 className="font-heading text-sm font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600">{item.desc}</p>
                  </span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-chat-check-line text-secondary-600" aria-hidden="true" />
                讨论区执行的四条规则
              </h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {discussionRules.map((rule) => (
                  <div key={rule.id} className="rounded-md bg-background-100 p-4">
                    <i className={`${rule.icon} text-lg text-primary-600`} aria-hidden="true" />
                    <h4 className="mt-2 font-heading text-sm font-bold text-foreground-950">{rule.title}</h4>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{rule.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </section>

        <section id="voices" className="w-full scroll-mt-24 bg-background-100 py-12 md:py-16">
          <div className="mx-auto w-full px-4 md:px-6">
            <Reveal>
              <SectionHeading
                eyebrow="Voices"
                title="读者怎么说"
                desc="以下内容来自长期读者的真实留言，我们保留了原话，没有做修饰。"
              />
            </Reveal>
            <div className="mt-7 grid grid-cols-1 gap-5 md:grid-cols-2">
              {aboutTestimonials.map((item, index) => (
                <Reveal key={item.id} delay={index * 65}>
                  <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                    <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true" />
                    <blockquote className="mt-3 flex-1 text-[13px] leading-relaxed text-foreground-700 md:text-sm">
                      {item.content}
                    </blockquote>
                    <figcaption className="mt-5 flex items-center gap-3 border-t border-background-100 pt-4">
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-background-50 ${item.avatarBg}`}
                        aria-hidden="true"
                      >
                        {item.user.slice(0, 1)}
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-semibold text-foreground-900">{item.user}</span>
                        <span className="text-[11px] text-foreground-400">{item.role}</span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="mx-auto w-full scroll-mt-24 px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-12">
            <Reveal>
              <SectionHeading
                eyebrow="FAQ"
                title="常见问题"
                desc="关于内容来源、代号、证据分级与投稿方式，这里都写清楚了。"
              />
            </Reveal>
            <Reveal delay={70}>
              <FaqAccordion items={faqItems} />
            </Reveal>
          </div>
        </section>

        <section id="contact" className="w-full scroll-mt-24 bg-background-100 py-12 md:py-16">
          <div className="mx-auto w-full px-4 md:px-6">
            <Reveal>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-9">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr] lg:gap-12">
                  <div>
                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.28em] text-primary-600">
                      <span className="h-px w-6 bg-primary-500" />
                      Contact
                    </span>
                    <h2 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-[30px]">
                      有事找我们，直接写邮件就好
                    </h2>
                    <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                      内容更正、隐私相关请求、投稿补充材料，都走同一个邮箱。为了能快速处理，
                      请在邮件里附上具体的页面地址和你的依据，通常一个工作日内会收到回复。
                    </p>

                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                      <a
                        href="mailto:contact@baoliaochigua.com"
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                      >
                        <i className="ri-mail-send-line" aria-hidden="true" />
                        contact@baoliaochigua.com
                      </a>
                      <Link
                        to="/community#submit"
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                      >
                        <i className="ri-quill-pen-line" aria-hidden="true" />
                        或者直接投稿
                      </Link>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    {[
                      { icon: 'ri-error-warning-line', title: '内容有误', desc: '附页面地址与更正依据，核实后我们会修改并标注时间。' },
                      { icon: 'ri-lock-2-line', title: '涉及隐私', desc: '请说明具体位置，我们会优先处理，不公开你的联系方式。' },
                      { icon: 'ri-time-line', title: '响应时间', desc: '工作日内一般 24 小时回复，复杂核实可能需要更久。' },
                    ].map((item) => (
                      <div key={item.title} className="flex gap-3 rounded-md border border-background-200 bg-background-100 p-4">
                        <i className={`${item.icon} mt-0.5 text-lg text-secondary-600`} aria-hidden="true" />
                        <span>
                          <strong className="block text-sm font-semibold text-foreground-900">{item.title}</strong>
                          <span className="mt-1 block text-xs leading-relaxed text-foreground-600">{item.desc}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}